import { StringParam, useQueryParams, withDefault } from "use-query-params";
import { Input } from "../../components/Input";
import { Button } from "../../components/Button";
import { faSearch } from "@fortawesome/free-solid-svg-icons/faSearch";
import React, { useCallback, useEffect, useState } from "react";
import cn from "classnames";
import { useTranslation } from "../../hooks/useTranslation";
import { InputSelect } from "../../components/InputSelect";
import { useContractors } from "../../contexts/ContractorsContext";
import { DateTime } from "luxon";
import { faDownload } from "@fortawesome/free-solid-svg-icons/faDownload";
import { SimpleViewModal } from "../../components/Modals/SimpleViewModal";
import { Preloader } from "../../components/Preloader";
import { useLoading } from "../../hooks/use-loading";
import { saveAs } from "file-saver";
import { useMusicApi } from "../../api/useMusicApi";

type Props = {
  isLoading: boolean;
  isEmpty: boolean;
  className?: string;
  onApply: (params: RightsIssuesFiltersParams) => Promise<any>;
  customInfo: string | null;
};

export type RightsIssuesFiltersParams = {
  title?: string | null;
  artist?: string | null;
  isrc?: Array<string>;
  external_id?: Array<string>;
  contractor_id?: string | number;
  limit?: number;
  offset?: number;
};

let DOWNLOAD_TIMEOUT: NodeJS.Timeout | number | null = null;
export const RightsIssuesFilters: React.FC<Props> = ({ isLoading, isEmpty, customInfo, className, onApply }) => {
  const { t, i18n } = useTranslation();
  const { startDownloadIssuesReport, checkPrepareDownloadReport, downloadReport } = useMusicApi();
  const { loadings, startLoading, stopLoading } = useLoading({
    report: false
  });
  const { contractors } = useContractors();
  const [query, setQuery] = useQueryParams({
    title: StringParam,
    artist: StringParam,
    isrc: StringParam,
    external_id: StringParam,
    contractor_id: withDefault(StringParam, contractors?.length === 1 ? contractors[0].id as any : undefined)
  });

  const [showDownloadIssuesReportModal, setShowDownloadIssuesReportModal] = useState<boolean>(false);
  useEffect(() => {
    if (contractors?.length > 1 && query.contractor_id != null || contractors?.length === 1) {
      void onApply(query as RightsIssuesFiltersParams);
    }
  }, []);

  useEffect(() => {
    document.onkeyup = function(e) {
      if (e.key === "Enter" && (e.target as any)?.tagName !== "TEXTAREA") {
        void onApply(query as RightsIssuesFiltersParams);
      }
    };

    return () => {
      document.onkeyup = null;
    };
  }, [query]);

  const startDownloadReport = useCallback(async () => {
      startLoading("report");
      try {
        const res = await startDownloadIssuesReport(query as any);
        await checkReportDownloadStatus(res);
      } catch (e) {
        stopLoading("report");
      }
    },
    [query]
  );

  const checkReportDownloadStatus = useCallback(async (taskId: string) => {
    try {
      let data = await checkPrepareDownloadReport(taskId);
      if (data !== "In progress") {
        let response = await downloadReport(data);
        const fileName = data.split("/")[1];
        saveAs(response, fileName);
        if (DOWNLOAD_TIMEOUT != null) {
          clearTimeout(DOWNLOAD_TIMEOUT as any);
        }
        stopLoading("report");
        setShowDownloadIssuesReportModal(false);
        return;
      }
      DOWNLOAD_TIMEOUT = setTimeout(
        () => checkReportDownloadStatus(taskId),
        5000
      );
    } catch (e) {
    }
  }, []);

  return (
    <div className={className}>
      <div className={cn("flex gap-2")}>
        {
          contractors.length > 1 &&
          <InputSelect
            className={"w-[250px]"}
            placeholder={"Контрагент"}
            value={query.contractor_id ?? null}
            options={contractors?.map(c => {
              return {
                value: c.id,
                label: c.title
              };
            }) ?? []}
            onChange={(v) => setQuery({ contractor_id: v.value?.toString() })}
          />
        }
        <Input
          style={{ width: "250px" }}
          defaultValue={query.title ?? ""}
          placeholder={`${t("search_by_track_release_title")}`}
          onChange={(v) =>
            setQuery({ title: v.trim().length > 0 ? v : null })
          }
        />

        <Input
          style={{ width: "270px" }}
          defaultValue={query.artist ?? ""}
          placeholder={`${t("search_by_artist_name")}`}
          onChange={(v) =>
            setQuery({ artist: v.trim().length > 0 ? v : null })
          }
        />

        <Button
          icon={faSearch}
          label={`${t("search")}`}
          onClick={() => onApply(query as RightsIssuesFiltersParams)}
        />
      </div>

      <div className={cn("flex gap-2 mt-2")}>
        <Input
          style={{ width: "250px" }}
          defaultValue={query.isrc ?? ""}
          placeholder={"ISRC"}
          onChange={(v) =>
            setQuery({ isrc: v.trim().length > 0 ? v : null })
          }
        />
        <Input
          style={{ width: "250px" }}
          defaultValue={query.external_id ?? ""}
          placeholder={`External ID`}
          onChange={(v) =>
            setQuery({ external_id: v.trim().length > 0 ? v : null })
          }
        />
        <Button disabled={isEmpty || isLoading} icon={faDownload} style={{ width: "270px" }} onClick={() => {
          startDownloadReport().then(() =>
            setShowDownloadIssuesReportModal(true)
          );
        }}>
          {t("issues.download_report")}
        </Button>
        {showDownloadIssuesReportModal && (
          <SimpleViewModal
            title={t("issues.report_is_downloading")}
            disableSuccess={loadings.report}
            className={cn("w-4/12")}
            onCancel={() => {
              if (DOWNLOAD_TIMEOUT != null) {
                clearTimeout(DOWNLOAD_TIMEOUT as any);
              }
              setShowDownloadIssuesReportModal(false);
            }}
          >
            <div className={"text-muted mb-4"}>
              {t("issues.report_is_downloading_warning")}
            </div>
            <Preloader size={"2x"} className={"my-2"} />
          </SimpleViewModal>
        )}

      </div>
      {
        customInfo != null && (
          <div className={"flex items-center font-[500] mt-3 gap-[12px]"}>
            <div>
              {t("issues.generated_info_by_period")} {DateTime.fromFormat(customInfo, "yyyy-LL-dd").setLocale(i18n.language).toFormat("LLLL yyyy")}
            </div>
          </div>
        )
      }
    </div>
  );
};
